import { useState, useEffect, useRef, useCallback } from "react";
import { createEdgeSpark } from "@edgespark/client";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Heart,
  Baby,
  Moon,
  Flower2,
  ShieldCheck,
  MapPin,
  ChevronDown,
  Phone,
  Mail,
  Menu,
  X,
  CheckCircle2,
  Clock,
  Home,
  Star,
} from "lucide-react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import BlogPage from "./pages/BlogPage";

/* ─── EdgeSpark Client ─── */
const edgesparkClient = createEdgeSpark({ baseUrl: "https://staging--rxecjvwj03kc8b71wwpn.youbase.cloud" });

/* ─── CDN Images ─── */
const HERO_IMG =
  "https://public.youware.com/image/ca2cbb7b-ef36-4aca-9bb0-d36bf3d5e26d/ujebngvl74.png";
const ABOUT_IMG =
  "https://public.youware.com/image/62eae88a-7da3-4bb9-b781-76ed33d06a54/3x3tainutn.jpeg";
const MOTHER_CHILD_IMG =
  "https://public.youware.com/image/d102e751-87db-46c8-b76b-f41443edc60c/j5v0t6c3ir.jpg";

/* ─── Service Illustrations ─── */
const ILLUS_BIRTH =
  "https://public.youware.com/users-website-assets/prod/8985b50c-139d-489a-a9c3-3fe9a94fa025/8ce9dfedd4a54ebd87163bbcff631b08.png";
const ILLUS_POSTPARTUM =
  "https://public.youware.com/users-website-assets/prod/8985b50c-139d-489a-a9c3-3fe9a94fa025/d5676e8ff20c48a58290f947356e50c4.png";
const ILLUS_LACTATION =
  "https://public.youware.com/users-website-assets/prod/8985b50c-139d-489a-a9c3-3fe9a94fa025/1008e5f096f941ecb7b716955a990e3f.png";
const ILLUS_NEWBORN =
  "https://public.youware.com/users-website-assets/prod/8985b50c-139d-489a-a9c3-3fe9a94fa025/9a30e7eaceeb47fd9962558c5882d3b3.png";

/* ─── Animated Section Wrapper ─── */
function FadeSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Wave Divider ─── */
function WaveDivider({ from, to, flip = false }: { from: string; to: string; flip?: boolean }) {
  return (
    <div className="wave-divider" style={{ background: from, transform: flip ? "scaleY(-1)" : "none" }}>
      <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ display: "block" }}>
        <path
          d="M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z"
          fill={to}
        />
      </svg>
    </div>
  );
}

/* ─── Gold Divider ─── */
function GoldDivider() {
  return <div className="gold-line w-24 mx-auto my-6" />;
}

/* ─── FAQ Data ─── */
const faqData = [
  {
    q: "Do you serve all of California?",
    a: "No. Blissful Arrival Doula Services serves only Sonoma County, California. This allows us to provide deeply personalized, local care to every family we support — from Santa Rosa to Petaluma, Healdsburg to Rohnert Park.",
  },
  {
    q: "Do you accept Medi-Cal in Sonoma County?",
    a: "Yes! If you have Medi-Cal coverage in Sonoma County, your doula services may be fully covered with $0 out-of-pocket cost. We'll help you verify your eligibility during your free consultation.",
  },
  {
    q: "Do you accept Kaiser Permanente in Sonoma County?",
    a: "Yes. We work with Kaiser Permanente members in Sonoma County. Contact us to verify your coverage and learn how doula support can be included in your care plan.",
  },
  {
    q: "Do you support hospital births in Santa Rosa?",
    a: "Absolutely. We provide support at hospitals in Santa Rosa and throughout Sonoma County, as well as birth centers and home births. We are with you wherever you choose to welcome your baby.",
  },
  {
    q: "When should I book a doula?",
    a: "We recommend connecting with us during your second trimester, but it's never too late. Whether you're early in pregnancy or approaching your due date, we welcome you with open arms.",
  },
  {
    q: "Is postpartum care included?",
    a: "We offer dedicated postpartum doula packages that include in-home recovery support, newborn guidance, breastfeeding help, emotional reassurance, and overnight care. These can be bundled or booked separately.",
  },
];

/* ─── Services Data ─── */
const services = [
  {
    icon: Heart,
    img: ILLUS_BIRTH,
    title: "Birth Doula Support",
    desc: "Continuous emotional and physical support during pregnancy and labor. We walk beside you every step of the way through your birth journey in Sonoma County.",
    color: "#8C6675",
  },
  {
    icon: Baby,
    img: ILLUS_POSTPARTUM,
    title: "Postpartum Care",
    desc: "In-home recovery support, newborn guidance, and emotional reassurance. Personalized care for Sonoma County families during the tender early weeks.",
    color: "#CFAE8F",
  },
  {
    icon: Flower2,
    img: ILLUS_LACTATION,
    title: "Lactation Support",
    desc: "Breastfeeding education and hands-on guidance to help you and baby find your rhythm with confidence and comfort.",
    color: "#8C6675",
  },
  {
    icon: Heart,
    img: ILLUS_NEWBORN,
    title: "Newborn & Emotional Support",
    desc: "Breastfeeding guidance, emotional reassurance, postpartum depression prevention support, and newborn care education.",
    color: "#CFAE8F",
  },
];

/* ─── Navigation Links ─── */
const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Insurance", href: "#insurance" },
  { label: "About", href: "#about" },
  { label: "Resources", href: "/blog" },
  { label: "Contact", href: "#contact" },
];

/* ══════════════════════════════════════════════════════ */
/* ═══                    APP                       ═══ */
/* ══════════════════════════════════════════════════════ */

/* ─── Nav Link Renderer ─── */
function NavItem({ href, label, className, onClick }: { href: string; label: string; className: string; onClick?: () => void }) {
  const location = useLocation();
  const isRoute = href.startsWith("/");
  const isHash = href.startsWith("#");
  const resolvedHref = isHash && location.pathname !== "/" ? `/${href}` : href;

  if (isRoute) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {label}
      </Link>
    );
  }
  return (
    <a href={resolvedHref} className={className} onClick={onClick}>
      {label}
    </a>
  );
}

export default function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", insurance: "", city: "", dueDate: "", message: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field as user types
    if (formErrors[name]) {
      setFormErrors((prev) => { const next = { ...prev }; delete next[name]; return next; });
    }
  };

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim();

    if (!trimmedName) errors.name = "Full name is required.";
    if (!trimmedEmail) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) errors.email = "Please enter a valid email address.";
    if (!trimmedPhone) errors.phone = "Phone number is required.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess(false);

    if (!validateForm()) return;
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const res = await edgesparkClient.api.fetch("/api/public/consultation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          insurance_provider: formData.insurance.trim(),
          city: formData.city.trim(),
          due_date: formData.dueDate.trim(),
          message: formData.message.trim(),
        }),
      });

      const result = await res.json();

      if (res.ok && result.success) {
        setSubmitSuccess(true);
        setFormData({ name: "", email: "", phone: "", insurance: "", city: "", dueDate: "", message: "" });
        setFormErrors({});
      } else if (result.errors) {
        if (result.errors._form) {
          setSubmitError(result.errors._form);
        } else {
          setFormErrors(result.errors);
        }
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } catch {
      setSubmitError("Unable to connect. Please check your internet and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory font-sans text-taupe" id="home">
      {/* ═══ TOP BAR ═══ */}
      <div className="bg-mauve text-white text-center text-sm py-2.5 font-medium tracking-wide">
        <div className="flex items-center justify-center gap-2">

          <span
            style={{
              fontWeight: "bold",
              fontStyle: "italic",
              fontSize: "20px"
            }}>Because every birth deserves to be blissful</span>
        </div>
      </div>
      {/* ═══ NAVIGATION ═══ */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg"
            : "bg-ivory"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full bg-mauve flex items-center justify-center">
              <Heart size={18} className="text-white" fill="white" />
            </div>
            <div>
              <span className="font-serif text-xl font-bold text-taupe tracking-tight">Blissful Arrival</span>
              <span className="block text-[10px] uppercase tracking-[0.2em] text-mauve font-semibold -mt-0.5">Doula Services</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <NavItem
                key={l.href}
                href={l.href}
                label={l.label}
                className="text-sm font-medium text-taupe/80 hover:text-mauve transition-colors relative group"
              />
            ))}
            <NavItem
              href="#contact"
              label="Free Consultation"
              className="btn-primary text-sm px-6 py-3"
            />
          </div>

          {/* Mobile Toggle */}
          <button className="lg:hidden text-taupe p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-blush overflow-hidden"
            >
              <div className="px-6 py-6 space-y-4">
                {navLinks.map((l) => (
                  <NavItem
                    key={l.href}
                    href={l.href}
                    label={l.label}
                    className="block text-base font-medium text-taupe hover:text-mauve transition-colors"
                    onClick={() => setMobileOpen(false)}
                  />
                ))}
                <NavItem
                  href="#contact"
                  label="Free Consultation"
                  className="btn-primary w-full text-center text-sm mt-4"
                  onClick={() => setMobileOpen(false)}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
      <Routes>
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/" element={<>

      {/* ═══ HERO SECTION ═══ */}
      <section className="relative overflow-hidden bg-ivory">
        {/* Organic blobs */}
        <div className="organic-blob w-96 h-96 bg-mauve top-10 -left-32" />
        <div className="organic-blob w-72 h-72 bg-gold bottom-20 right-10" />

        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text */}
            <FadeSection>
              <div className="space-y-6 relative z-10">
                <div className="flex items-center gap-2 text-sm font-semibold text-mauve uppercase tracking-widest">
                  <MapPin size={14} />
                  <span>Sonoma County, California</span>
                </div>
                <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-taupe leading-[1.1]">
                  Rooted in Support.{" "}
                  <span className="text-mauve italic">Centered on You.</span>
                </h1>
                <p className="text-lg text-taupe/75 leading-relaxed max-w-lg">
                  Compassionate, culturally aware birth and postpartum care for families in Sonoma County. You deserve to feel heard, protected, and empowered.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <a href="#contact" className="btn-primary">
                    Schedule Your Free Consultation
                  </a>
                  <a href="#insurance" className="btn-secondary">
                    Verify Medi-Cal or Kaiser Coverage
                  </a>
                </div>
              </div>
            </FadeSection>

            {/* Hero Image */}
            <FadeSection delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-mauve/10 to-gold/10 rounded-4xl blur-2xl" />
                <img
                  src={HERO_IMG}
                  alt="Beautiful Black pregnant woman in Sonoma County - Blissful Arrival Doula Services"
                  className="relative rounded-4xl shadow-2xl w-full object-cover aspect-[3/4] lg:aspect-[4/5] animate-float"
                />
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <ShieldCheck size={22} className="text-gold" />
                  </div>
                  <div>
                    <p className="font-semibold text-taupe text-sm">Medi-Cal & Kaiser</p>
                    <p className="text-xs text-taupe/60">Coverage Accepted</p>
                  </div>
                </div>
              </div>
            </FadeSection>
          </div>
        </div>

        <WaveDivider from="#F8F5F3" to="#F2E9ED" />
      </section>

      {/* ═══ LOCAL TRUST STRIP ═══ */}
      <section className="bg-blush py-12 relative">
        <div className="max-w-6xl mx-auto px-6">
          <FadeSection>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { icon: MapPin, label: "Proudly Serving\nSonoma County" },
                { icon: ShieldCheck, label: "Medi-Cal\nAccepted" },
                { icon: Star, label: "Kaiser Permanente\nAccepted" },
                { icon: Home, label: "Hospital, Birth Center" },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-2xl bg-white shadow-md flex items-center justify-center">
                    <item.icon size={24} className="text-mauve" />
                  </div>
                  <p className="text-sm font-semibold text-taupe whitespace-pre-line leading-snug">{item.label}</p>
                </div>
              ))}
            </div>
          </FadeSection>
        </div>
      </section>

      <WaveDivider from="#F2E9ED" to="#F8F5F3" />

      {/* ═══ SERVICES SECTION ═══ */}
      <section id="services" className="bg-ivory py-20 lg:py-28 relative">
        <div className="organic-blob w-80 h-80 bg-gold -right-20 top-20" />
        <div className="organic-blob w-64 h-64 bg-mauve -left-16 bottom-20" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <FadeSection>
            <div className="text-center mb-16">
              <p className="text-sm font-semibold text-mauve uppercase tracking-widest mb-3">Our Offerings</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-taupe">
                Services for Sonoma County Families
              </h2>
              <GoldDivider />
              <p className="text-taupe/70 max-w-2xl mx-auto mt-4 text-lg">
                Every service is designed to nurture, protect, and empower you through pregnancy, birth, and beyond — right here in Sonoma County.
              </p>
            </div>
          </FadeSection>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {services.map((s, i) => (
              <FadeSection key={i} delay={i * 0.1}>
                <div
                  className="premium-card bg-white rounded-3xl p-8 lg:p-10 shadow-lg relative overflow-hidden group"
                  style={{ marginTop: i % 2 !== 0 ? "2rem" : "0" }}
                >
                  {/* Decorative accent */}
                  <div
                    className="absolute top-0 right-0 w-32 h-32 rounded-bl-[60px] opacity-10"
                    style={{ background: s.color }}
                  />
                  <div className="flex items-start gap-5 mb-6">
                    <img
                      src={s.img}
                      alt={`${s.title} illustration`}
                      className="w-24 h-24 lg:w-28 lg:h-28 object-contain flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-taupe mb-3">{s.title}</h3>
                  <p className="text-taupe/70 leading-relaxed">{s.desc}</p>
                  <div className="gold-line w-16 mt-6" />
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider from="#F8F5F3" to="#8C6675" />

      {/* ═══ INSURANCE SECTION ═══ */}
      <section id="insurance" className="bg-mauve py-20 lg:py-28 relative overflow-hidden">
        <div className="organic-blob w-96 h-96 bg-white/10 -right-32 top-0" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeSection>
              <div className="space-y-6">
                <p className="text-sm font-semibold text-gold uppercase tracking-widest">Coverage & Access</p>
                <h2 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight">
                  Medi-Cal & Kaiser Coverage Available in Sonoma County
                </h2>
                <GoldDivider />
                <p className="text-white/85 text-lg leading-relaxed">
                  If you are covered under Medi-Cal or Kaiser Permanente in Sonoma County, your doula services may be fully covered with <strong className="text-gold">$0 out-of-pocket cost</strong>.
                </p>
                <a href="#contact" className="inline-flex items-center gap-2 bg-gold text-taupe font-semibold px-8 py-4 rounded-full hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                  Verify My Coverage
                </a>
              </div>
            </FadeSection>

            <FadeSection delay={0.2}>
              <div className="space-y-6">
                {[
                  { step: "01", title: "Schedule Consultation", desc: "Book your free consultation to discuss your needs and birth plan." },
                  { step: "02", title: "Verify Sonoma County Coverage", desc: "We'll help confirm your Medi-Cal or Kaiser eligibility in Sonoma County." },
                  { step: "03", title: "Begin Personalized Care", desc: "Start receiving compassionate, culturally aware doula support tailored to you." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-5 items-start bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
                    <span className="text-3xl font-serif font-bold text-gold">{item.step}</span>
                    <div>
                      <h4 className="font-semibold text-white text-lg">{item.title}</h4>
                      <p className="text-white/70 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      <WaveDivider from="#8C6675" to="#F8F5F3" />

      {/* ═══ ABOUT SECTION ═══ */}
      <section id="about" className="bg-ivory py-20 lg:py-28 relative">
        <div className="organic-blob w-72 h-72 bg-mauve -left-20 top-10" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image side */}
            <FadeSection>
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-tr from-gold/15 to-mauve/10 rounded-4xl blur-xl" />
                <img
                  src={ABOUT_IMG}
                  alt="Black doula providing compassionate birth support in Sonoma County"
                  className="relative rounded-4xl shadow-2xl w-full object-cover aspect-[4/5]"
                />
                {/* Overlapping small image */}
                <div className="absolute -bottom-6 -right-6 w-40 h-40 md:w-52 md:h-52 rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                  <img
                    src={MOTHER_CHILD_IMG}
                    alt="Black mother and child — Sonoma County doula support"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </FadeSection>

            {/* Text side */}
            <FadeSection delay={0.15}>
              <div className="space-y-6">
                <p className="text-sm font-semibold text-mauve uppercase tracking-widest">About Blissful Arrival</p>
                <h2 className="font-serif text-4xl sm:text-5xl font-bold text-taupe leading-tight">
                  Care Rooted in Dignity and Cultural Understanding
                </h2>
                <GoldDivider />
                <div className="space-y-4 text-taupe/80 text-lg leading-relaxed">
                  <p>
                    Blissful Arrival Doula Services was founded to provide culturally respectful, emotionally safe, and evidence-informed care to families in Sonoma County.
                  </p>
                  <p>
                    As a Black doula, I understand the importance of representation, trust, and advocacy in maternal care. Every family deserves to feel heard, protected, and empowered during birth.
                  </p>
                  <p className="font-serif text-2xl text-mauve italic font-semibold pt-2">
                    Your story matters. Your birth matters.
                  </p>
                </div>
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      <WaveDivider from="#F8F5F3" to="#EFE3DB" />

      {/* ═══ FAQ SECTION ═══ */}
      <section id="faq" className="bg-cream py-20 lg:py-28 relative">
        <div className="max-w-3xl mx-auto px-6">
          <FadeSection>
            <div className="text-center mb-14">
              <p className="text-sm font-semibold text-mauve uppercase tracking-widest mb-3">Common Questions</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-taupe">
                Frequently Asked Questions
              </h2>
              <GoldDivider />
            </div>
          </FadeSection>

          <div className="space-y-4">
            {faqData.map((faq, i) => (
              <FadeSection key={i} delay={i * 0.05}>
                <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-blush/40 transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-taupe pr-4">{faq.q}</span>
                    <ChevronDown
                      size={20}
                      className={`text-mauve flex-shrink-0 transition-transform duration-300 ${
                        openFaq === i ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >

                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider from="#EFE3DB" to="#F8F5F3" />

      {/* ═══ CONTACT SECTION ═══ */}
      <section id="contact" className="bg-ivory py-20 lg:py-28 relative">
        <div className="organic-blob w-80 h-80 bg-gold -right-20 bottom-10" />
        <div className="organic-blob w-64 h-64 bg-mauve -left-16 top-10" />

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <FadeSection>
            <div className="text-center mb-14">
              <p className="text-sm font-semibold text-mauve uppercase tracking-widest mb-3">Get In Touch</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold text-taupe">
                Begin Your Journey with Support in Sonoma County
              </h2>
              <GoldDivider />
              <p className="text-taupe/70 max-w-2xl mx-auto mt-4 text-lg">
                Schedule your free consultation today. We'd love to learn about your story and how we can support your family.
              </p>
            </div>
          </FadeSection>

          <FadeSection delay={0.15}>
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 space-y-6"
              noValidate
            >
              {/* Success Message */}
              <AnimatePresence>
                {submitSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-3 p-5 bg-green-50 border border-green-200 rounded-2xl"
                  >
                    <CheckCircle2 size={22} className="text-green-600 flex-shrink-0" />
                    <p className="text-green-800 font-medium text-sm">
                      Thank you! Your consultation request has been sent successfully. We will contact you soon.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error Message */}
              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                  <p className="text-red-700 text-sm font-medium">{submitError}</p>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-taupe mb-2">Full Name <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-5 py-3.5 rounded-xl border-2 bg-ivory/50 text-taupe placeholder:text-taupe/40 focus:border-mauve focus:ring-0 outline-none transition-colors ${formErrors.name ? 'border-red-300' : 'border-blush'}`}
                    placeholder="Your full name"
                  />
                  {formErrors.name && <p className="text-red-500 text-xs mt-1.5 ml-1">{formErrors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-taupe mb-2">Email <span className="text-red-400">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-5 py-3.5 rounded-xl border-2 bg-ivory/50 text-taupe placeholder:text-taupe/40 focus:border-mauve focus:ring-0 outline-none transition-colors ${formErrors.email ? 'border-red-300' : 'border-blush'}`}
                    placeholder="your@email.com"
                  />
                  {formErrors.email && <p className="text-red-500 text-xs mt-1.5 ml-1">{formErrors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-taupe mb-2">Phone <span className="text-red-400">*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-5 py-3.5 rounded-xl border-2 bg-ivory/50 text-taupe placeholder:text-taupe/40 focus:border-mauve focus:ring-0 outline-none transition-colors ${formErrors.phone ? 'border-red-300' : 'border-blush'}`}
                    placeholder="(707) 555-0123"
                  />
                  {formErrors.phone && <p className="text-red-500 text-xs mt-1.5 ml-1">{formErrors.phone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-taupe mb-2">Insurance Provider</label>
                  <select
                    name="insurance"
                    value={formData.insurance}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 rounded-xl border-2 border-blush bg-ivory/50 text-taupe focus:border-mauve focus:ring-0 outline-none transition-colors"
                  >
                    <option value="">Select your insurance</option>
                    <option value="medi-cal">Medi-Cal</option>
                    <option value="kaiser">Kaiser Permanente</option>
                    <option value="other">Other / Private Pay</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-taupe mb-2">City in Sonoma County</label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 rounded-xl border-2 border-blush bg-ivory/50 text-taupe focus:border-mauve focus:ring-0 outline-none transition-colors"
                  >
                    <option value="">Select your city</option>
                    <option value="santa-rosa">Santa Rosa</option>
                    <option value="petaluma">Petaluma</option>
                    <option value="rohnert-park">Rohnert Park</option>
                    <option value="windsor">Windsor</option>
                    <option value="healdsburg">Healdsburg</option>
                    <option value="cotati">Cotati</option>
                    <option value="sebastopol">Sebastopol</option>
                    <option value="sonoma">Sonoma</option>
                    <option value="cloverdale">Cloverdale</option>
                    <option value="other">Other Sonoma County Area</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-taupe mb-2">Due Date</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 rounded-xl border-2 border-blush bg-ivory/50 text-taupe focus:border-mauve focus:ring-0 outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-taupe mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-5 py-3.5 rounded-xl border-2 border-blush bg-ivory/50 text-taupe placeholder:text-taupe/40 focus:border-mauve focus:ring-0 outline-none transition-colors resize-none"
                  placeholder="Tell us about your pregnancy journey and how we can support you..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`btn-primary w-full text-lg py-5 transition-all duration-300 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-0.5'}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Schedule Your Free Consultation'
                )}
              </button>
              <p className="text-center text-sm text-taupe/50">
                Serving Sonoma County exclusively — Santa Rosa, Petaluma, Healdsburg, and beyond.
              </p>
            </form>
          </FadeSection>
        </div>
      </section>

      <WaveDivider from="#F8F5F3" to="#8C6675" />

        </>} />
      </Routes>
      {/* ═══ FOOTER ═══ */}
      <footer className="bg-mauve text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Heart size={18} className="text-white" fill="white" />
                </div>
                <span className="font-serif text-xl font-bold">Blissful Arrival</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Compassionate, culturally aware doula services exclusively for Sonoma County families. Medi-Cal and Kaiser Permanente accepted.
              </p>
            </div>

            {/* Links */}
            <div className="space-y-4">
              <h4 className="font-serif text-lg font-semibold">Quick Links</h4>
              <div className="space-y-2">
                {navLinks.map((l) => (
                  <a key={l.href} href={l.href} className="block text-sm text-white/70 hover:text-gold transition-colors">
                    {l.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h4 className="font-serif text-lg font-semibold">Contact</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <MapPin size={16} className="text-gold flex-shrink-0" />
                  <span>Sonoma County, California</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <Mail size={16} className="text-gold flex-shrink-0" />
                  <span>Blissfularrivals@gmail.com</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-white/70">
                  <Phone size={16} className="text-gold flex-shrink-0" />
                  <span>(707) 241-0949</span>
                </div>
              </div>
            </div>
          </div>

          <div className="gold-line w-full mt-12 mb-8 opacity-30" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
            <p>&copy; {new Date().getFullYear()} Blissful Arrival Doula Services. All rights reserved.</p>
            <p>Proudly serving Sonoma County —.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
